import { c as _c } from "react/compiler-runtime";
import { BlobServiceClient } from '@azure/storage-blob';
import { v4 as uuid } from 'uuid';
import { useMutation } from '@tanstack/react-query';
import { useRecordState } from '~/utils/react';
import type { Media } from '@funkey/api/src/features';
import useNotifications from '../notifications/useNotifications';
import { useI18n } from '~/features/i18n';
import { action_getUploadURL } from './actions';

/** @internal */
export default function useUploadFiles() {
  const $ = _c(9);
  const i18n = useI18n();
  const {
    dispatchError
  } = useNotifications();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const progress = useRecordState(t0);
  let t1;
  if ($[1] !== dispatchError || $[2] !== i18n || $[3] !== progress) {
    t1 = {
      throwOnError: false,
      async mutationFn(data) {
        if (!data.enrichedFiles?.length) {
          throw new Error(i18n._("No file selected"));
        }
        const url = await action_getUploadURL(data.entity);
        if (!url) {
          throw new Error(i18n._("Invalid upload URL"));
        }
        const containerClient = new BlobServiceClient(url).getContainerClient("");
        return Promise.all(data.enrichedFiles.map(async file => {
          const originalName = file.name;
          const name = uniqueFileName(originalName);
          const blobClient = containerClient.getBlockBlobClient(uploadPath(data.entity, name));
          await blobClient.uploadData(file, {
            blobHTTPHeaders: {
              blobContentType: file.type
            },
            onProgress: event => {
              progress.setKey(file.name, event.loadedBytes / file.size * 100);
            }
          });
          return {
            ...data.entity,
            type: file.type,
            originalName,
            name,
            path: blobClient.url.split("?")[0] ?? "",
            size: file.size
          };
        }));
      },
      onError(error) {
        dispatchError(error);
      }
    };
    $[1] = dispatchError;
    $[2] = i18n;
    $[3] = progress;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const uploadFiles = useMutation(t1);
  let t2;
  if ($[5] !== progress || $[6] !== uploadFiles.isPending || $[7] !== uploadFiles.mutateAsync) {
    t2 = {
      isPending: uploadFiles.isPending,
      mutateAsync: uploadFiles.mutateAsync,
      progress
    };
    $[5] = progress;
    $[6] = uploadFiles.isPending;
    $[7] = uploadFiles.mutateAsync;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
const uploadPath = (entitity: Media.EntityFK, name: string) => {
  let path = `${entitity.attachableType}/`;
  if (entitity.attachableId) path += `${entitity.attachableId}/`;
  return `${path}${name}`;
};
function uniqueFileName(fileName: string) {
  const baseName = fileName.split('.')[0];
  const extension = fileName.split('.')[1];
  const uniqueId = uuid();
  return `${baseName}_${uniqueId}.${extension}`;
}